import { useEffect, useMemo, useState } from "react";
import { Button, Space, Typography } from "antd";
import { ArrowRightOutlined, LinkOutlined, SafetyCertificateOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

const blockedProtocols = new Set(["javascript:", "data:", "vbscript:", "file:", "blob:"]);

const isIosDevice = () => {
  if (typeof navigator === "undefined") return false;
  const platform = navigator.platform || "";
  const userAgent = navigator.userAgent || "";
  const touchPoints = navigator.maxTouchPoints || 0;
  return /iPad|iPhone|iPod/i.test(userAgent) || (platform === "MacIntel" && touchPoints > 1);
};

const isAndroidDevice = () =>
  typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

const deviceTarget = () => {
  if (isIosDevice()) return "ios";
  if (isAndroidDevice()) return "android";
  return "web";
};

const cleanTarget = (value) => {
  const target = String(value || "").trim();
  if (!target) return "";

  try {
    const parsed = new URL(target, window.location.origin);
    if (blockedProtocols.has(parsed.protocol.toLowerCase())) return "";
    return target;
  } catch {
    return "";
  }
};

const targetLabel = {
  web: "Web",
  ios: "iOS",
  android: "Android",
};

const SafeLinkRedirect = () => {
  const [status, setStatus] = useState("Preparing your link...");

  const { target, targetType, fallbackTarget } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const links = {
      web: cleanTarget(params.get("web")),
      ios: cleanTarget(params.get("ios")),
      android: cleanTarget(params.get("android")),
    };
    const nextTargetType = deviceTarget();
    const nextTarget = links[nextTargetType] || links.web || links.ios || links.android || "";

    return {
      target: nextTarget,
      targetType: nextTargetType,
      fallbackTarget: links.web || links.ios || links.android || "",
    };
  }, []);

  useEffect(() => {
    if (!target) {
      setStatus("This link is missing a destination.");
      return undefined;
    }

    setStatus(`Opening ${targetLabel[targetType]} destination...`);
    const timeout = window.setTimeout(() => {
      window.location.replace(target);
    }, 450);

    return () => window.clearTimeout(timeout);
  }, [target, targetType]);

  const openTarget = () => {
    if (fallbackTarget) {
      window.location.assign(fallbackTarget);
    }
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background:
          "radial-gradient(circle at 20% 18%, rgba(0, 153, 102, 0.18), transparent 32%), linear-gradient(135deg, #f7fffb 0%, #eef8ff 100%)",
      }}
    >
      <section
        style={{
          width: "min(460px, 100%)",
          border: "1px solid rgba(0, 89, 137, 0.16)",
          borderRadius: 18,
          padding: 28,
          background: "rgba(255, 255, 255, 0.92)",
          boxShadow: "0 24px 70px rgba(0, 56, 86, 0.16)",
          textAlign: "center",
        }}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div
            style={{
              width: 58,
              height: 58,
              margin: "0 auto",
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: "#009966",
              background: "linear-gradient(145deg, #ffffff, #e9fff6)",
              boxShadow: "0 12px 28px rgba(0, 153, 102, 0.22)",
            }}
          >
            <SafetyCertificateOutlined style={{ fontSize: 28 }} />
          </div>
          <div>
            <Title level={3} style={{ margin: 0, color: "#005989" }}>
              Opening your link
            </Title>
            <Text style={{ color: "#009966", fontSize: 16 }}>{status}</Text>
          </div>
          {target ? (
            <Button
              type="primary"
              size="large"
              icon={<ArrowRightOutlined />}
              onClick={() => window.location.assign(target)}
              style={{ borderRadius: 999, background: "#009966", borderColor: "#009966" }}
            >
              Open now
            </Button>
          ) : (
            <Button
              size="large"
              icon={<LinkOutlined />}
              onClick={openTarget}
              disabled={!fallbackTarget}
              style={{ borderRadius: 999, borderColor: "#005989", color: "#005989" }}
            >
              Open fallback
            </Button>
          )}
        </Space>
      </section>
    </main>
  );
};

export default SafeLinkRedirect;
